import { Button, Image, LibCombobox, lucideIcons, Skeleton, useComboboxAnchor } from '@round/creatorbase-ui';
import { SpotifyArtistItem, getSpotifyArtistUrl, useSpotifyArtistCombobox } from './useSpotifyArtistCombobox';
import { CSSProperties } from 'react';
import styles from './SpotifyArtistFilter.module.css';
import { useSelectedSpotifyArtists } from './useSelectedSpotifyArtists';
import cn from 'classnames';

const { X } = lucideIcons;

type Props = {
    value: string[];
    onChange: (ids: string[]) => void;
};

const ArtistItemLabel = ({ item }: { item: SpotifyArtistItem }) => {
    const subtitle = item.genres?.length
        ? item.genres.slice(0, 3).join(', ')
        : item.followers
          ? `${item.followers.toLocaleString()} followers`
          : undefined;

    return (
        <div className={styles.item}>
            <Image src={item.image_url || ''} className={styles.thumbnail} />
            <div className={styles.itemLabels}>
                <span className={styles.itemTitle}>{item.label}</span>
                {subtitle && <span className={styles.itemSubtitle}>{subtitle}</span>}
            </div>
        </div>
    );
};

export function SpotifyArtistFilter({ value: selectedArtistSpotifyIds, onChange }: Props) {
    const { items: value, isFetching: areSelectedArtistsFetching } =
        useSelectedSpotifyArtists(selectedArtistSpotifyIds);

    const { items, isFetching, isError, isSearching, filter, onInputValueChange, onItemsListScroll } =
        useSpotifyArtistCombobox();

    const anchor = useComboboxAnchor();

    const noItemsFound = isError
        ? 'Failed to load artists'
        : isFetching
          ? 'Loading...'
          : isSearching
            ? 'No artists found'
            : 'Type to search artists...';

    const handleSelect = (selected: SpotifyArtistItem[] | null) => {
        onChange((selected ?? []).map((a) => a.value));
        onInputValueChange('');
    };

    const handleRemove = (spotifyId: string) => {
        onChange(selectedArtistSpotifyIds.filter((id) => id !== spotifyId));
    };

    return (
        <div className={styles.container}>
            <LibCombobox
                multiple
                value={value}
                onInputValueChange={onInputValueChange}
                debounce={500}
                items={items}
                isItemEqualToValue={(a, b) => a?.value === b?.value}
                onValueChange={handleSelect}
                filter={filter}
            >
                <div ref={anchor}>
                    <LibCombobox.Input placeholder="Search Spotify artists..." showTrigger={false} />
                </div>
                <LibCombobox.Content
                    anchor={anchor}
                    style={{ '--item-count': items.length } as CSSProperties}
                    className={cn({ [styles.forceContentHeight]: !!items.length })}
                >
                    <LibCombobox.Empty>{noItemsFound}</LibCombobox.Empty>
                    <LibCombobox.List onScroll={onItemsListScroll}>
                        {(item: SpotifyArtistItem) => (
                            <LibCombobox.Item key={item.value} value={item}>
                                <ArtistItemLabel item={item} />
                            </LibCombobox.Item>
                        )}
                    </LibCombobox.List>
                </LibCombobox.Content>
            </LibCombobox>

            {selectedArtistSpotifyIds.length > 0 && (
                <div className={styles.selectedList} role="list">
                    {selectedArtistSpotifyIds.map((id, i) => {
                        const artist = value.find((a) => a.value === id);
                        const isLoading = !artist && areSelectedArtistsFetching;
                        return (
                            <div key={artist?.value || i} className={styles.selectedItem} role="listitem">
                                {isLoading ? (
                                    <Skeleton className={cn(styles.artistName, styles.artistNameSkeleton)} />
                                ) : (
                                    <a
                                        href={artist?.spotify_url ?? getSpotifyArtistUrl(id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.artistName}
                                    >
                                        {artist?.label ?? id}
                                    </a>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={styles.removeButton}
                                    disabled={isLoading}
                                    onClick={() => handleRemove(id)}
                                >
                                    <X />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
